(function(window, fullscreenControlViewFactory) {
   'use strict';

   var namespace = window.TSC = window.TSC || {};
   namespace.FullscreenControlView = fullscreenControlViewFactory(window,
      window.$,
      namespace.templates,
      namespace.localizationStrings,
      namespace.deviceInfo,
      namespace.cssClasses,
      namespace.playerConfiguration,
      namespace.iFrameBridge,
      namespace.events,
      namespace.tabIndex,
      namespace.urlService);
})(this, function(window,
                  $,
                  templates,
                  localizationStrings,
                  deviceInfo,
                  cssClasses,
                  playerConfiguration,
                  iFrameBridge,
                  events,
                  tabIndex,
                  urlService) {
   'use strict';

   var FULL_SCREEN_BUTTON_MODE = 'fullScreenMode';
   var FULL_FRAME_BUTTON_MODE = 'fullFrameMode';
   var EXPAND_BUTTON_MODE = 'expandMode';
   var HIDDEN_BUTTON_MODE = 'hiddenMode';

   /**
    * @memberof TSC
    * @class FullscreenControlView
    * @classdesc Full Screen Control View
    */
   var FullscreenControlView = function($container) {
      var _inFullScreen = false;
      var _inFullFrame = false;
      var _$document = $(window.document);
      var _$fullScreenButton;
      var _$fullScreenGlyph;
      var _buttonMode = null;

      var findButtonMode = function() {
         if (deviceInfo.supportsFullScreen()) {
            _buttonMode = FULL_SCREEN_BUTTON_MODE;
         } else if (iFrameBridge.isInIframe()) {
            _buttonMode = playerConfiguration.getDisableFullframeMode() ? EXPAND_BUTTON_MODE : FULL_FRAME_BUTTON_MODE;
         } else {
            _buttonMode = HIDDEN_BUTTON_MODE;
         }
      };

      var createView = function() {
         var buttonTitle = localizationStrings.strings.accessBtnFullScreen;
         if (_buttonMode === EXPAND_BUTTON_MODE) {
            buttonTitle = localizationStrings.strings.accessBtnFullScreenExpand;
         }

         var _markup = templates.fullscreen_control_template({
            accessBtnFullScreen: buttonTitle,
            fullscreenIndex: tabIndex.Fullscreen
         });

         $container.find('.fullscreen-wrapper').html(_markup);
         _$fullScreenButton = $container.find('.fullscreen-button');
         _$fullScreenGlyph = $container.find('.fullscreen-button-glyph');

         if (_buttonMode === HIDDEN_BUTTON_MODE) {
            _$fullScreenButton.hide();
         }
      };

      var onFullScreenChange = function() {
         _inFullScreen = !_inFullScreen;

         if (_inFullScreen) {
            _$fullScreenGlyph.removeClass(cssClasses.fullscreenButtonGlyph);
            _$fullScreenGlyph.addClass(cssClasses.fullscreenButtonActiveGlyph);
            _$fullScreenButton.attr('aria-pressed', true);

            _$fullScreenButton.focus();

            $container.addClass(cssClasses.fullscreenMode);
         } else {
            _$fullScreenGlyph.removeClass(cssClasses.fullscreenButtonActiveGlyph);
            _$fullScreenGlyph.addClass(cssClasses.fullscreenButtonGlyph);
            _$fullScreenButton.attr('aria-pressed', false);

            $container.removeClass(cssClasses.fullscreenMode);
         }

         $container.trigger(events.Controls.FullScreenChange, {inFullScreen: _inFullScreen});
      };

      var initializeFullScreenSupport = function(canSupportExpandVideoMode) {
         switch (_buttonMode) {
            case FULL_SCREEN_BUTTON_MODE:
               var fullScreenEventNames = deviceInfo.getFullScreenChangeEventNames();
               _$document.off(fullScreenEventNames, onFullScreenChange);
               _$document.on(fullScreenEventNames, onFullScreenChange);
               break;
            case FULL_FRAME_BUTTON_MODE:
               _$fullScreenGlyph.removeClass(cssClasses.fullscreenButtonGlyph);
               _$fullScreenGlyph.addClass(cssClasses.fullFrameButtonGlyph);
               _$fullScreenButton.show();
               break;
            case EXPAND_BUTTON_MODE:
               if (canSupportExpandVideoMode) {
                  _$fullScreenGlyph.removeClass(cssClasses.fullscreenButtonGlyph);
                  _$fullScreenGlyph.addClass(cssClasses.expandButtonGlyph);
                  _$fullScreenButton.show();
               } else {
                  _$fullScreenButton.hide();
               }
               break;
         }
      };

      var cancelFullScreen = function() {
         _$fullScreenButton.attr('aria-pressed', false);
         if (window.document.exitFullscreen) {
            window.document.exitFullscreen();
         } else if (window.document.webkitExitFullscreen) {
            window.document.webkitExitFullscreen();
         } else if (window.document.msExitFullscreen) {
            window.document.msExitFullscreen();
         } else if (window.document.mozCancelFullScreen) {
            window.document.mozCancelFullScreen();
         }
      };

      var switchToFullScreenMode = function() {
         _$fullScreenButton.attr('aria-pressed', true);
         var myPlayer = $container.get(0);

         if (myPlayer.requestFullscreen) {
            myPlayer.requestFullscreen();
         } else if (myPlayer.webkitRequestFullscreen) {
            myPlayer.webkitRequestFullscreen();
         } else if (myPlayer.msRequestFullscreen) {
            myPlayer.msRequestFullscreen();
         } else if (myPlayer.mozRequestFullScreen) {
            myPlayer.mozRequestFullScreen();
         }
      };

      var switchFromFullScreenMode = function() {
         cancelFullScreen();
      };

      var toggleFullScreenMode = function() {
         if (!_inFullScreen) {
            switchToFullScreenMode();
         } else {
            switchFromFullScreenMode();
         }
      };

      var toggleFullFrameMode = function() {
         _inFullFrame = !_inFullFrame;
         if (_inFullFrame) {
            _$fullScreenGlyph.removeClass(cssClasses.fullFrameButtonGlyph);
            _$fullScreenGlyph.addClass(cssClasses.fullFrameButtonActiveGlyph);
            _$fullScreenButton.attr('aria-pressed', true);
            iFrameBridge.postMessage(events.IFrame.EnterFullFrame, '*');
         } else {
            _$fullScreenGlyph.removeClass(cssClasses.fullFrameButtonActiveGlyph);
            _$fullScreenGlyph.addClass(cssClasses.fullFrameButtonGlyph);
            iFrameBridge.postMessage(events.IFrame.ExitFullFrame, '*');
            _$fullScreenButton.attr('aria-pressed', false);
         }
      };

      var expandVideo = function() {
         $container.trigger(events.Controls.ExpandVideo);
         urlService.openUrl(urlService.generateCurrentUrlWithExtraQueryParam('theme', playerConfiguration.getTheme()), '_blank');
      };

      var onFullScreenClick = function() {
         switch (_buttonMode) {
            case FULL_SCREEN_BUTTON_MODE:
               toggleFullScreenMode();
               break;
            case FULL_FRAME_BUTTON_MODE:
               toggleFullFrameMode();
               break;
            case EXPAND_BUTTON_MODE:
               expandVideo();
               break;
         }
      };

      var onDoubleClick = function() {
         switch (_buttonMode) {
            case FULL_SCREEN_BUTTON_MODE:
               toggleFullScreenMode();
               break;
            case FULL_FRAME_BUTTON_MODE:
               toggleFullFrameMode();
               break;
         }
      };

      var initializeHandlers = function() {
         _$fullScreenButton.click(function(e) {
            onFullScreenClick();
            e.preventDefault();
         });

         var disableControl = function() {
            _$fullScreenButton.attr('tabindex', tabIndex.Disabled);
         };

         var enableControl = function() {
            _$fullScreenButton.attr('tabindex', tabIndex.Fullscreen);
         };

         $container.on(events.Controls.Disable, disableControl);
         $container.on(events.Controls.Enable, enableControl);
      };

      var onVideoEnd = function() {
         // must kick them out of full screen to get the replay button to show
         if (_inFullScreen) {
            cancelFullScreen();
         }
      };

      findButtonMode();
      createView();
      initializeHandlers();

      return Object.defineProperties({
         initializeFullScreenSupport: initializeFullScreenSupport,
         onVideoEnd: onVideoEnd,
         onDoubleClick: onDoubleClick
      }, {
         isFullScreen: {
            get: function() {
               return _inFullScreen;
            }
         },
         isFullFrame: {
            get: function() {
               return _inFullFrame;
            }
         },
         controlElement: {
            get: function() {
               return _$fullScreenButton;
            }
         }
      });
   };

   return {
      /**
       *  Factory method that returns a new FullscreenControlView object.
       *  @function create
       *  @memberof TSC.FullscreenControlView
       *  @static
       *  @param {Object} $container - jQuery element that contains a .fullscreen-wrapper element where the fullscreen controls will be injected
       *  @param {Object} mediaElement - html5 media DOM element (or wrapper presenting same interface)
       *  @return new FullscreenControlView instance
       */
      create: FullscreenControlView
   };
});
